import { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import promoImage from '@/assets/promo-popup.jpg.asset.json';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLocation } from 'react-router-dom';

const STORAGE_KEY = 'velireo_promo_popup_dismissed_at';
const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

const PromoPopup = () => {
  const { login } = useAuth();
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const prevFocus = useRef<HTMLElement | null>(null);
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw && Date.now() - Number(raw) < SEVEN_DAYS) return;
    } catch {
      /* ignore */
    }
    prevFocus.current = document.activeElement as HTMLElement | null;
    setOpen(true);
  }, []);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setOpen(false);
    prevFocus.current?.focus?.();
  }, []);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeBtnRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        dismiss();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const items = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, dismiss]);

  if (!open) return null;

  const handleLineLogin = async () => {
    dismiss();
    try {
      const next = pathname && pathname.startsWith('/') && !pathname.startsWith('//') ? pathname : '/';
      await login(next);
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-3"
      style={{ backgroundColor: 'rgba(46,42,39,.55)', backdropFilter: 'blur(2px)' }}
      onClick={dismiss}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="promo-popup-title"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[432px] overflow-hidden"
        style={{
          backgroundColor: '#FFFDF9',
          borderRadius: 16,
          boxShadow: '0 24px 60px rgba(46,42,39,.28)',
          animation: reduceMotion ? undefined : 'promo-pop-in 200ms ease-out',
        }}
      >
        <style>{`@keyframes promo-pop-in{from{opacity:0;transform:scale(.96)}to{opacity:1;transform:scale(1)}}`}</style>

        <button
          ref={closeBtnRef}
          type="button"
          onClick={dismiss}
          aria-label="關閉優惠彈窗"
          className="absolute right-3 top-3 flex items-center justify-center rounded-full"
          style={{ width: 44, height: 44, backgroundColor: 'rgba(255,255,255,.92)', color: '#2B2B2B' }}
        >
          <X size={16} strokeWidth={2} />
        </button>

        <img
          src={promoImage.url}
          alt=""
          aria-hidden="true"
          className="w-full object-cover aspect-[16/9] sm:aspect-[16/8]"
        />

        <div className="px-6 pb-6 pt-5 text-center">
          <p
            className="font-sans-tc"
            style={{ fontSize: 12, letterSpacing: '.22em', color: '#6B5F57' }}
          >
            VELIREO SKIN
          </p>

          <h2
            id="promo-popup-title"
            className="mt-3 font-sans-tc font-bold"
            style={{
              fontSize: 19,
              lineHeight: 1.5,
              color: '#2B2B2B',
              wordBreak: 'keep-all',
            }}
          >
            <span className="sm:text-[25px]">
              首次購買 <span style={{ color: '#B4635B' }}>9 折</span>
              <br />滿 1,500 免運費
            </span>
          </h2>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-2 gap-y-2 font-sans-tc">
            <span className="inline-flex items-center gap-2">
              <span
                className="inline-flex items-center justify-center rounded-full font-bold"
                style={{ width: 19, height: 19, backgroundColor: '#F5EDE7', color: '#9E5049', fontSize: 11 }}
              >
                1
              </span>
              <span style={{ fontSize: isMobile ? 12 : 13, color: '#6B5F57' }}>用 LINE 登入註冊</span>
            </span>
            <span aria-hidden="true" style={{ color: '#C9AFA6', fontSize: isMobile ? 12 : 13 }}>›</span>
            <span className="inline-flex items-center gap-2">
              <span
                className="inline-flex items-center justify-center rounded-full font-bold"
                style={{ width: 19, height: 19, backgroundColor: '#F5EDE7', color: '#9E5049', fontSize: 11 }}
              >
                2
              </span>
              <span style={{ fontSize: isMobile ? 12 : 13, color: '#6B5F57' }}>9 折券自動入帳</span>
            </span>
          </div>

          <button
            type="button"
            onClick={handleLineLogin}
            className="mt-5 flex w-full items-center justify-center font-sans-tc font-bold"
            style={{
              backgroundColor: '#C9817A',
              color: '#FFFFFF',
              fontSize: 19,
              padding: 16,
              borderRadius: 10,
              gap: isMobile ? 12 : 13,
            }}
          >
            <span
              className="inline-flex shrink-0 items-center justify-center"
              style={{
                width: isMobile ? 25 : 27,
                height: isMobile ? 25 : 27,
                backgroundColor: '#FFFFFF',
                borderRadius: 7,
              }}
              aria-hidden="true"
            >
              <svg width={isMobile ? 17 : 19} height={isMobile ? 17 : 19} viewBox="0 0 24 24" fill="#06C755">
                <path d="M12 2.7c5.2 0 9.4 3.4 9.4 7.6 0 1.7-.7 3.2-2 4.7-1.9 2.2-6.1 4.9-7.1 5.3-1 .4-.9-.3-.8-.6l.1-.8c0-.3.1-.7-.2-1-.3-.1-.9-.2-1.4-.3-4.1-.6-7.4-3.5-7.4-7.3C2.6 6.1 6.8 2.7 12 2.7z" />
              </svg>
            </span>
            <span style={{ whiteSpace: 'nowrap' }}>
              {isMobile ? 'LINE 登入領 9 折' : '用 LINE 登入領 9 折券'}
            </span>
          </button>

          <p className="mt-3 font-sans-tc" style={{ fontSize: 12, color: '#7D7066' }}>
            首次註冊會員適用 · 30 天內有效
          </p>
        </div>
      </div>
    </div>
  );
};

export default PromoPopup;
